import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckoutConfirmationResponseType } from 'src/models/Checkout/Confirmation';
import { useTHelper } from 'src/utils/i18n';
import { CheckoutConfirmationTable } from 'src/pages/Checkout/components/common/Table';

type Props = {
  model: CheckoutConfirmationResponseType['AuctionBidsSubSection'];
};

export const CheckoutConfirmationAuctionBids = memo<Props>(({ model }) => {
  const tHelper = useTHelper('checkout.confirmation.auctionBids');

  const { t } = useTranslation();

  const columns = [
    { name: model.ItemsTerminology.Singular || '', className: 'left' },
    model.ShowFairMarketValueColumn && { name: tHelper('fairMarketValue'), className: 'left' },
    { name: tHelper('yourBid'), className: 'right' },
    model.ShowMaximumBidColumn && { name: tHelper('yourMaximumBid'), className: 'right' },
    { name: tHelper('status'), className: 'right' },
  ];

  // BidStatusCode
  const getStatusCode = useCallback(
    (statusId: number): string => {
      switch (statusId) {
        case 1:
          return t('common.bidStatusCodes.draft');
        case 2:
          return t('common.bidStatusCodes.winning');
        case 3:
          return t('common.bidStatusCodes.losing');
        case 4:
          return t('common.bidStatusCodes.paymentEmailSent');
        case 5:
          return t('common.bidStatusCodes.failed');
        case 6:
          return t('common.bidStatusCodes.won');
        case 7:
          return t('common.bidStatusCodes.cancelled');
        case 8:
          return t('common.bidStatusCodes.revoked');
        case 9:
          return t('common.bidStatusCodes.lost');
        case 10:
          return t('common.bidStatusCodes.voided');
        case 11:
          return t('common.bidStatusCodes.paymentAuthenticationRequired');
        default:
          return '';
      }
    },
    [t]
  );

  return (
    <>
      <h2 className="section-header">{model.SectionHeader}</h2>
      <CheckoutConfirmationTable columns={columns} data={model.Items}>
        {(item) => (
          <>
            <td className="left">{item.AuctionItemName}</td>
            {model.ShowFairMarketValueColumn && <td className="left">{item.FairMarketValueString}</td>}
            <td className="right">{item.CurrentBidAmountString}</td>
            {model.ShowMaximumBidColumn && <td className="right">{item.MaximumBidAmountString}</td>}
            <td className="right">{getStatusCode(item.DisplayStatusID)}</td>
          </>
        )}
      </CheckoutConfirmationTable>
    </>
  );
});
